import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FunctionsOutlinedIcon from "@mui/icons-material/FunctionsOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import SpaceBarOutlinedIcon from "@mui/icons-material/SpaceBarOutlined";
import { Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from "@mui/material";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectSelectedStatementLineId } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectMeasures } from "../../../../store/selectors/metaDataSelectors.ts";
import type { StatementLineState } from "../../../../store/types/fieldsState.types.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import type { MenuLineType } from "../utils/statementRowsArea.ts";
import { createAmountLine, createMenuLine, findAmountMeasure, getNewLineAnchor } from "../utils/statementRowsArea.ts";

interface LineTypeMenuItemProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

const LineTypeMenuItem = ({ icon, label, onClick }: LineTypeMenuItemProps) => (
  <MenuItem onClick={onClick}>
    <ListItemIcon>{icon}</ListItemIcon>
    <ListItemText>{label}</ListItemText>
  </MenuItem>
);

interface Props {
  anchorEl: HTMLElement;
  lineLabels: string[];
  onAddFormula: () => void;
  onClose: () => void;
}

const AddLineMenu = ({ anchorEl, lineLabels, onAddFormula, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const selectedLineId = useSelector(selectSelectedStatementLineId);
  const measures = useSelector(selectMeasures);

  const amountMeasure = findAmountMeasure(measures);

  const addLine = (line: StatementLineState) => {
    onClose();
    dispatch(createFinancialStatementAction("addLine", { line, anchor: getNewLineAnchor(selectedLineId) }));
  };

  const handleAddAmount = () => {
    if (!amountMeasure) {
      onClose();
      return;
    }
    addLine(createAmountLine(amountMeasure));
  };

  const handleAddMenuLine = (lineType: MenuLineType) => addLine(createMenuLine(lineType, lineLabels));

  return (
    <Menu open anchorEl={anchorEl} onClose={onClose}>
      <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Line Type</ListSubheader>
      <LineTypeMenuItem icon={<AttachMoneyOutlinedIcon color="secondary" />} label="Amount" onClick={handleAddAmount} />
      <LineTypeMenuItem
        icon={<FolderOutlinedIcon color="secondary" />}
        label="Group"
        onClick={() => handleAddMenuLine("Group")}
      />
      <Divider />
      <LineTypeMenuItem
        icon={<FunctionsOutlinedIcon color="secondary" />}
        label="Subtotal of Children"
        onClick={() => handleAddMenuLine("Subtotal")}
      />
      <LineTypeMenuItem
        icon={<PlaylistAddCheckOutlinedIcon color="secondary" />}
        label="Total of Lines"
        onClick={() => handleAddMenuLine("Total")}
      />
      <Divider />
      <LineTypeMenuItem
        icon={<CalculateOutlinedIcon color="secondary" />}
        label="Custom Formula"
        onClick={onAddFormula}
      />
      <Divider />
      <LineTypeMenuItem
        icon={<SpaceBarOutlinedIcon color="secondary" />}
        label="Spacer"
        onClick={() => handleAddMenuLine("Spacer")}
      />
    </Menu>
  );
};

export default AddLineMenu;
